import React, { useState, useEffect } from "react";
import { all_routes } from "../../../router/all_routes";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import AddClass from "./addClass";
import ModifyClass from "./modifyClass";
import { API } from "../../../../common/Constants";
import useFetch from "../../../../hooks/useFetch";
import {
  getAPIHeaders,
  getFormattedDateDDMMYYYY,
  isCurrentDateInBetween,
  isBeforeCurrentDate,
  toCamelCase,
} from "../../../../common/Utils";
import PreLoader from "../../../../core/common/preLoader";
import { Class, Curriculum360 } from "../../../../types/dataModel";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import { useAccess } from "../../../../hooks/accessContext";
import DeleteModal from "../../../../core/modals/deleteModal";
import CustomToast from "../../../../core/common/customToast";
import "../../../../style/css/shared.scss";

const Classes = () => {
  const { hasAcademicMgntEdit, hasClassMgntCreate, hasClassMgntDelete } =
    useAccess();
  const navigation = useNavigate();
  const { isLoading, callFetch } = useFetch();
  const [classes, setClasses] = useState<Class[]>([]);
  const [filteredClasses, setFilteredClasses] = useState<Class[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [currCourse, setCurriculumCourse] = useState<Curriculum360[]>([]);
  const [editId, setEditId] = useState<number>(0);
  const [deleteItem, setDeleteItem] = useState<any>({
    id: undefined,
    message: undefined,
  });
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [classStudents, setClassStudents] = useState<any[]>([]);
  const [classTeachers, setClassTeachers] = useState<any[]>([]);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [viewType, setViewType] = useState<"table" | "card">("table");
  const [totalBatches, setTotalBatches] = useState<number>(0);
  const [activeBatches, setActiveBatches] = useState<number>(0);
  const [inactiveBatches, setInactiveBatches] = useState<number>(0);
  const [upcomingBatches, setUpcomingBatches] = useState<number>(0);

  useEffect(() => {
    fetchClass();
    fetchCurriculum360();
  }, []);

  useEffect(() => {
    if (classes) {
      setTotalBatches(classes.length);
      const active = classes.filter(
        (item: any) => item.curriculumStatus === "ACTIVE"
      ).length;
      const inactive = classes.filter(
        (item: any) => item.curriculumStatus === "INACTIVE"
      ).length;
      const upcoming = classes.filter(
        (item: any) => item.curriculumStatus === "UPCOMING"
      ).length;

      setActiveBatches(active);
      setInactiveBatches(inactive);
      setUpcomingBatches(upcoming);
    }
  }, [classes]);

  useEffect(() => {
    if (classes) {
      if (activeFilter === "active") {
        setFilteredClasses(
          classes.filter((item: any) => item.curriculumStatus === "ACTIVE")
        );
      } else if (activeFilter === "inactive") {
        setFilteredClasses(
          classes.filter((item: any) => item.curriculumStatus === "INACTIVE")
        );
      } else if (activeFilter === "upcoming") {
        setFilteredClasses(
          classes.filter((item: any) => item.curriculumStatus === "UPCOMING")
        );
      } else {
        setFilteredClasses(classes);
      }
    }
  }, [activeFilter, classes]);

  const fetchClass = () => {
    callFetch(API.CLASS_SUBJECT, "GET", getAPIHeaders(), null, null).then(
      (res) => {
        setClasses(parseToClasses(res));
      }
    );
  };

  const fetchCurriculum360 = () => {
    callFetch(API.CURRICULUM_360, "GET", getAPIHeaders(), null, null).then(
      (res) => {
        setCurriculumCourse(parseToCurrCourse(res));
      }
    );
  };

  const deleteData = (id: number) => {
    callFetch(`${API.CLASS}/${id}`, "DELETE", getAPIHeaders(), null, null)
      .then(() => {
        fetchClass();
      })
      .catch((error) => {
        setToastSettings((prev) => ({
          ...prev,
          show: true,
          message:
            error?.response?.data ||
            "Something went wrong, please try again later.",
        }));
      });
  };

  const handleViewClassDetails = async (batchId: number) => {
    setSelectedClass(classes.find((c) => c.batchId === batchId));

    try {
      const studentsResponse = await callFetch(
        `${API.CLASS}/${batchId}/students`,
        "GET",
        getAPIHeaders(),
        null,
        null
      );

      setClassStudents(studentsResponse || []);

      const teachersResponse = await callFetch(
        `${API.CLASS}/${batchId}/teachers`,
        "GET",
        getAPIHeaders(),
        null,
        null
      );

      setClassTeachers(teachersResponse || []);
    } catch (error) {
      console.error("Error fetching class details:", error);
      setToastSettings({
        show: true,
        message: "Failed to load class details. Please try again later.",
        heading: "Error Loading Details",
        type: "ERROR",
      });
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "batchId",
      render: (text: String, record: any) => (
        <h6 className="fw-medium">{text}</h6>
      ),
      sorter: (a: any, b: any) => a.batchId - b.batchId,
    },
    {
      title: "Class",
      dataIndex: "batchName",
      render: (text: String, record: any) => (
        <div>
          <h6 className="fw-bold mb-0 class-title">
            <Link
              to="#"
              onClick={() => {
                setEditId(record?.batchId);
              }}
              data-bs-toggle="modal"
              data-bs-target="#edit_class"
            >
              {record.batchName}
            </Link>
          </h6>
          <span className="badge badge-soft-secondary mt-1">
            Section-{record.batchSection}
          </span>
        </div>
      ),
      sorter: (a: any, b: any) => a.batchName.localeCompare(b.batchName),
      defaultSortOrder: "ascend",
    },
    {
      title: "Status",
      dataIndex: "curriculumStatus",
      render: (text: string) =>
        text ? (
          text === "ACTIVE" ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center badge-sm">
              {text}
            </span>
          ) : text === "INACTIVE" ? (
            <span className="badge badge-soft-danger d-inline-flex align-items-center badge-sm">
              {text}
            </span>
          ) : (
            <span className="badge badge-soft-info d-inline-flex align-items-center badge-sm">
              {text}
            </span>
          )
        ) : null,
      sorter: (a: any, b: any) => {
        const statusOrder: { [key: string]: number } = {
          ACTIVE: 1,
          UPCOMING: 2,
          INACTIVE: 3,
        };
        return (
          statusOrder[a.curriculumStatus] - statusOrder[b.curriculumStatus]
        );
      },
      defaultSortOrder: "ascend",
    },
    {
      title: "Curriculum",
      dataIndex: "curriculumTitle",
      render: (text: String, record: any) =>
        text ? (
          <div className="d-flex align-items-center">
            <div className="ms-2">
              <p className="text-dark mb-0">
                <h6 className="fw-medium">{text}</h6>
              </p>

              <span className="fs-12">
                <span className="fs-12 fw-bold text-dark me-1">Start:</span>
                {getFormattedDateDDMMYYYY(record.curriculumStartDate)} -
                <span className="fs-12 fw-bold text-dark me-1 ms-1">End:</span>
                {getFormattedDateDDMMYYYY(record.curriculumEndDate)}
              </span>
              <p className="fs-12 fw-bold text-dark mb-0">
                Status:
                {isCurrentDateInBetween(
                  record.curriculumStartDate,
                  record.curriculumEndDate
                ) ? (
                  <span className="text-success"> Live</span>
                ) : isBeforeCurrentDate(record.curriculumEndDate) ? (
                  <span className="text-danger"> Expired</span>
                ) : (
                  <span className="text-info"> Upcoming</span>
                )}
              </p>
            </div>
          </div>
        ) : null,
      sorter: (a: any, b: any) =>
        a.curriculumTitle?.localeCompare(b.curriculumTitle),
    },
    {
      title: "Course",
      dataIndex: "courseTitle",
      sorter: (a: any, b: any) => a.courseTitle?.localeCompare(b.courseTitle),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any) => {
        return (
          <div className="action-icon d-inline-flex">
            <Link
              to="#"
              className="me-2 icon-details"
              title="View Students & Teachers"
              data-bs-toggle="modal"
              data-inert={true}
              data-bs-target="#class_details_modal"
              onClick={() => handleViewClassDetails(record?.batchId)}
            >
              <i className="ti ti-users-group" />
            </Link>
            <button
              className="btn btn-sm btn-secondary me-2"
              onClick={() => {
                navigation(
                  `${all_routes.timetable}?id=${record?.batchId}&curriculumCourseId=${record?.curriculumCourseId}`
                );
              }}
              title="Manage Timetable"
            >
              Timetable
            </button>
            <Link
              hidden={!hasAcademicMgntEdit()}
              to="#"
              className="me-2 icon-edit"
              data-bs-toggle="modal"
              data-inert={true}
              data-bs-target="#edit_class"
              onClick={() => {
                setEditId(record?.batchId);
              }}
              title="Edit Class"
            >
              <i
                className={`ti ${
                  record.curriculumStatus === "INACTIVE"
                    ? "ti-arrow-up text-success fs-25"
                    : "ti-edit"
                }`}
              />
            </Link>

            <Link
              hidden={
                !hasClassMgntDelete() || record.curriculumStatus === "INACTIVE"
              }
              to="#"
              className="icon-delete"
              data-bs-toggle="modal"
              data-inert={true}
              data-bs-target="#delete_modal"
              onClick={() =>
                setDeleteItem({
                  id: record.batchId,
                  message: `Are you sure you want to delete ${record.batchName}? This can't be undone.`,
                })
              }
              title="Delete Class"
            >
              <i className="ti ti-trash" />
            </Link>
          </div>
        );
      },
    },
  ];

  const handleFilterClick = (filter: string | null) => {
    if (activeFilter === filter) {
      setActiveFilter(null);
    } else {
      setActiveFilter(filter);
    }
  };

  const renderAddClass = () => {
    return (
      <AddClass
        currCoursesOpts={parseToOption(currCourse)}
        onSuccess={fetchClass}
      />
    );
  };

  const renderEditClass = () => {
    return (
      <ModifyClass
        currCoursesOpts={parseToOption(currCourse)}
        onSuccess={fetchClass}
        courseClass={classes?.find((el: Class) => el.batchId === editId)}
      />
    );
  };

  const renderStatCards = () => {
    return (
      <div className="row">
        <div className="col-xl-3 col-md-6">
          <div
            className={`card flex-fill position-relative ${
              activeFilter === null ? "bg-light border-primary" : ""
            }`}
            style={{ cursor: "pointer" }}
            onClick={() => handleFilterClick(null)}
          >
            <span className="position-absolute start-0 bottom-0">
              <img
                className="img-fluid"
                alt="img"
                src="../../../assets/img/reports-img/total-expense.svg"
              />
            </span>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <span className="fs-14 fw-normal text-truncate mb-1">
                    Total Batches
                  </span>
                  <h5>{totalBatches}</h5>
                </div>
                <span className="avatar avatar-md avatar-rounded bg-transparent-primary border border-primary">
                  <span className="text-primary">
                    <i className="ti ti-users"></i>
                  </span>
                </span>
              </div>
              {activeFilter === null && (
                <div className="badge badge-soft-primary mt-2">
                  Current Filter
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div
            className={`card flex-fill position-relative ${
              activeFilter === "active" ? "bg-light border-success" : ""
            }`}
            style={{ cursor: "pointer" }}
            onClick={() => handleFilterClick("active")}
          >
            <span className="position-absolute start-0 bottom-0">
              <img
                className="img-fluid"
                alt="img"
                src="../../../assets/img/reports-img/approved-expense.svg"
              />
            </span>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <span className="fs-14 fw-normal text-truncate mb-1">
                    Active Batches
                  </span>
                  <h5>{activeBatches}</h5>
                </div>
                <span className="avatar avatar-md avatar-rounded bg-transparent-success border border-success">
                  <span className="text-success">
                    <i className="ti ti-check"></i>
                  </span>
                </span>
              </div>
              {activeFilter === "active" && (
                <div className="badge badge-soft-success mt-2">
                  Current Filter
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div
            className={`card flex-fill position-relative ${
              activeFilter === "inactive" ? "bg-light border-danger" : ""
            }`}
            style={{ cursor: "pointer" }}
            onClick={() => handleFilterClick("inactive")}
          >
            <span className="position-absolute start-0 bottom-0">
              <img
                className="img-fluid"
                alt="img"
                src="../../../assets/img/reports-img/reject-expense.svg"
              />
            </span>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <span className="fs-14 fw-normal text-truncate mb-1">
                    Inactive Batches
                  </span>
                  <h5>{inactiveBatches}</h5>
                </div>
                <span className="avatar avatar-md avatar-rounded bg-transparent-danger border border-danger">
                  <span className="text-danger">
                    <i className="ti ti-x"></i>
                  </span>
                </span>
              </div>
              {activeFilter === "inactive" && (
                <div className="badge badge-soft-danger mt-2">
                  Current Filter
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div
            className={`card flex-fill position-relative ${
              activeFilter === "upcoming" ? "bg-light border-info" : ""
            }`}
            style={{ cursor: "pointer" }}
            onClick={() => handleFilterClick("upcoming")}
          >
            <span className="position-absolute start-0 bottom-0">
              <img
                className="img-fluid"
                alt="img"
                src="../../../assets/img/reports-img/pending-expense.svg"
              />
            </span>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <span className="fs-14 fw-normal text-truncate mb-1">
                    Upcoming Batches
                  </span>
                  <h5>{upcomingBatches}</h5>
                </div>
                <span className="avatar avatar-md avatar-rounded bg-transparent-info border border-info">
                  <span className="text-info">
                    <i className="ti ti-clock"></i>
                  </span>
                </span>
              </div>
              {activeFilter === "upcoming" && (
                <div className="badge badge-soft-info mt-2">Current Filter</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderClassDetailsModal = () => {
    return (
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {selectedClass ? (
                <>
                  Class Details: {selectedClass.batchName}
                  <span className="badge badge-soft-secondary ms-2">
                    Section-{selectedClass.batchSection}
                  </span>
                </>
              ) : (
                "Class Details"
              )}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <>
              {selectedClass && (
                <div className="class-info mb-4">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card border">
                        <div className="card-body">
                          <h6 className="card-title">Class Information</h6>
                          <div className="row">
                            <div className="col-6 mb-2">
                              <div className="fs-12 text-muted">Class Name</div>
                              <div className="fw-medium">
                                {selectedClass.batchName}
                              </div>
                            </div>
                            <div className="col-6 mb-2">
                              <div className="fs-12 text-muted">Section</div>
                              <div className="fw-medium">
                                {selectedClass.batchSection}
                              </div>
                            </div>
                            <div className="col-12 mb-2">
                              <div className="fs-12 text-muted">
                                Current Status
                              </div>
                              <div>
                                {isCurrentDateInBetween(
                                  selectedClass.curriculumStartDate,
                                  selectedClass.curriculumEndDate
                                ) ? (
                                  <span className="text-success">Live</span>
                                ) : isBeforeCurrentDate(
                                    selectedClass.curriculumEndDate
                                  ) ? (
                                  <span className="text-danger">Expired</span>
                                ) : (
                                  <span className="text-info">Upcoming</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border">
                        <div className="card-body">
                          <h6 className="card-title">
                            Curriculum Information
                            {selectedClass.curriculumStatus === "ACTIVE" ? (
                              <span className="badge badge-soft-success ms-2">
                                Active
                              </span>
                            ) : selectedClass.curriculumStatus ===
                              "INACTIVE" ? (
                              <span className="badge badge-soft-danger ms-2">
                                Inactive
                              </span>
                            ) : (
                              <span className="badge badge-soft-info ms-2">
                                Upcoming
                              </span>
                            )}
                          </h6>
                          <div className="row">
                            <div className="col-6 mb-2">
                              <div className="fs-12 text-muted">Curriculum</div>
                              <div className="fw-medium">
                                {selectedClass.curriculumTitle}
                              </div>
                            </div>
                            <div className="col-6 mb-2">
                              <div className="fs-12 text-muted">Course</div>
                              <div className="fw-medium">
                                {selectedClass.courseTitle}
                              </div>
                            </div>
                            <div className="col-6 mb-2">
                              <div className="fs-12 text-muted">Start Date</div>
                              <div className="fw-medium">
                                {getFormattedDateDDMMYYYY(
                                  selectedClass.curriculumStartDate
                                )}
                              </div>
                            </div>
                            <div className="col-6 mb-2">
                              <div className="fs-12 text-muted">End Date</div>
                              <div className="fw-medium">
                                {getFormattedDateDDMMYYYY(
                                  selectedClass.curriculumEndDate
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <ul
                className="nav nav-tabs mb-3"
                id="classDetailsTab"
                role="tablist"
              >
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="students-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#students-tab-pane"
                    type="button"
                    role="tab"
                    aria-controls="students-tab-pane"
                    aria-selected="true"
                  >
                    Students
                    <span className="badge rounded-pill bg-light text-dark ms-1">
                      {classStudents?.length || 0}
                    </span>
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="teachers-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#teachers-tab-pane"
                    type="button"
                    role="tab"
                    aria-controls="teachers-tab-pane"
                    aria-selected="false"
                  >
                    Teachers
                    <span className="badge rounded-pill bg-light text-dark ms-1">
                      {classTeachers?.length || 0}
                    </span>
                  </button>
                </li>
              </ul>

              <div className="tab-content" id="classDetailsTabContent">
                <div
                  className="tab-pane fade show active"
                  id="students-tab-pane"
                  role="tabpanel"
                  aria-labelledby="students-tab"
                  tabIndex={0}
                >
                  {classStudents && classStudents.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Student ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Roll Number</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classStudents.map((student, index) => (
                            <tr key={index}>
                              <td>{student.accountId}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  {student.profilePictureUrl ? (
                                    <div className="avatar avatar-sm me-3 flex-shrink-0">
                                      <img
                                        src={student.profilePictureUrl}
                                        alt={`${student.firstName} ${student.lastName}`}
                                        className="avatar-img rounded-circle"
                                      />
                                    </div>
                                  ) : (
                                    <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                      <span className="text-primary">
                                        {student.firstName?.charAt(0)}
                                        {student.lastName?.charAt(0)}
                                      </span>
                                    </div>
                                  )}
                                  <span className="fw-medium">{`${student.firstName} ${student.lastName}`}</span>
                                </div>
                              </td>
                              <td>{student.email}</td>
                              <td>{student.rollNumber}</td>
                              <td>
                                <span
                                  className={`badge badge-soft-${
                                    student.status === "ACTIVE"
                                      ? "success"
                                      : "danger"
                                  }`}
                                >
                                  {student.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar avatar-lg bg-light-primary mb-3">
                        <span className="text-primary">
                          <i className="ti ti-users"></i>
                        </span>
                      </div>
                      <h6>No Students Found</h6>
                      <p className="text-muted">
                        No students are currently assigned to this class.
                      </p>
                    </div>
                  )}
                </div>

                <div
                  className="tab-pane fade"
                  id="teachers-tab-pane"
                  role="tabpanel"
                  aria-labelledby="teachers-tab"
                  tabIndex={0}
                >
                  {classTeachers && classTeachers.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Teacher ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Subjects</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classTeachers.map((teacher, index) => (
                            <tr key={index}>
                              <td>{teacher.accountId}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  {teacher.profilePictureUrl ? (
                                    <div className="avatar avatar-sm me-3 flex-shrink-0">
                                      <img
                                        src={teacher.profilePictureUrl}
                                        alt={`${teacher.firstName} ${teacher.lastName}`}
                                        className="avatar-img rounded-circle"
                                      />
                                    </div>
                                  ) : (
                                    <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                      <span className="text-primary">
                                        {teacher.firstName?.charAt(0)}
                                        {teacher.lastName?.charAt(0)}
                                      </span>
                                    </div>
                                  )}
                                  <span className="fw-medium">{`${teacher.firstName} ${teacher.lastName}`}</span>
                                </div>
                              </td>
                              <td>{teacher.email}</td>
                              <td>
                                {teacher.subjects ? (
                                  <div>
                                    {teacher.subjects.map(
                                      (subject: string, idx: number) => (
                                        <span
                                          key={idx}
                                          className="badge bg-light text-dark me-1 mb-1"
                                        >
                                          {subject}
                                        </span>
                                      )
                                    )}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>
                                <span
                                  className={`badge badge-soft-${
                                    teacher.status === "ACTIVE"
                                      ? "success"
                                      : "danger"
                                  }`}
                                >
                                  {teacher.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar avatar-lg bg-light-primary mb-3">
                        <span className="text-primary">
                          <i className="ti ti-user"></i>
                        </span>
                      </div>
                      <h6>No Teachers Found</h6>
                      <p className="text-muted">
                        No teachers are currently assigned to this class.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <DeleteModal
        heading="Confirm Delete"
        message={deleteItem.message}
        onDelete={() => deleteData(deleteItem?.id)}
      />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Classes</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Learning Center</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Classes
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <button
                  disabled={!hasClassMgntCreate()}
                  data-bs-toggle="modal"
                  data-inert={true}
                  data-bs-target="#add_class"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Class
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {renderStatCards()}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Class List</h5>
              <div className="view-toggle">
                <button
                  className={`btn btn-sm me-2 ${
                    viewType === "table" ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setViewType("table")}
                  aria-label="Switch to table view"
                >
                  <i className="ti ti-table me-1"></i> Table View
                </button>
                <button
                  className={`btn btn-sm ${
                    viewType === "card" ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setViewType("card")}
                  aria-label="Switch to card view"
                >
                  <i className="ti ti-layout-grid me-1"></i> Card View
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === "table" ? (
                <Table
                  dataSource={filteredClasses}
                  columns={columns}
                  Selection={false}
                  refresh={fetchClass}
                />
              ) : (
                <div className="class-cards p-3">
                  <div className="row">
                    {filteredClasses.map((classItem: any) => (
                      <div
                        className="col-xl-4 col-lg-6 col-md-6 mb-3"
                        key={classItem.batchId}
                      >
                        <div className="card h-100 border shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                  <span className="text-primary fw-bold">
                                    {classItem.batchName
                                      .charAt(0)
                                      .toUpperCase()}
                                  </span>
                                </div>
                                <h6 className="mb-0 fw-semibold class-title">
                                  <Link
                                    to="#"
                                    className="text-reset text-decoration-none"
                                    onClick={() => {
                                      setEditId(classItem?.batchId);
                                    }}
                                    data-bs-toggle="modal"
                                    data-bs-target="#edit_class"
                                  >
                                    {classItem.batchName}
                                  </Link>
                                </h6>
                              </div>
                              <span className="badge badge-soft-primary">
                                Section-{classItem.batchSection}
                              </span>
                            </div>

                            <div className="d-flex justify-content-between mb-3">
                              <div>
                                <div className="fs-12 fw-medium text-dark">
                                  Curriculum
                                </div>
                                <div className="fs-12">
                                  {classItem.curriculumTitle}
                                </div>
                              </div>
                              <div>
                                <div className="fs-12 fw-medium text-dark">
                                  Course
                                </div>
                                <div className="fs-12">
                                  {classItem.courseTitle}
                                </div>
                              </div>
                            </div>

                            <div className="d-flex justify-content-between mb-3">
                              <div>
                                <div className="fs-12 fw-medium text-dark">
                                  Start Date
                                </div>
                                <div className="fs-12">
                                  {getFormattedDateDDMMYYYY(
                                    classItem.curriculumStartDate
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="fs-12 fw-medium text-dark">
                                  End Date
                                </div>
                                <div className="fs-12">
                                  {getFormattedDateDDMMYYYY(
                                    classItem.curriculumEndDate
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="fs-12 fw-medium text-dark">
                                  Status
                                </div>
                                <div>
                                  {classItem.curriculumStatus === "ACTIVE" ? (
                                    <span className="badge badge-soft-success badge-sm">
                                      Active
                                    </span>
                                  ) : classItem.curriculumStatus ===
                                    "INACTIVE" ? (
                                    <span className="badge badge-soft-danger badge-sm">
                                      Inactive
                                    </span>
                                  ) : (
                                    <span className="badge badge-soft-info badge-sm">
                                      Upcoming
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="class-status fs-12 mb-3">
                              <div className="fw-medium text-dark mb-1">
                                Current Status:
                              </div>
                              {isCurrentDateInBetween(
                                classItem.curriculumStartDate,
                                classItem.curriculumEndDate
                              ) ? (
                                <div className="d-flex align-items-center">
                                  <span className="dot-indicator bg-success me-1"></span>
                                  <span className="text-success">Live</span>
                                </div>
                              ) : isBeforeCurrentDate(
                                  classItem.curriculumEndDate
                                ) ? (
                                <div className="d-flex align-items-center">
                                  <span className="dot-indicator bg-danger me-1"></span>
                                  <span className="text-danger">Expired</span>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <span className="dot-indicator bg-info me-1"></span>
                                  <span className="text-info">Upcoming</span>
                                </div>
                              )}
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                              <button
                                className="btn btn-sm btn-outline-info me-2"
                                onClick={() =>
                                  handleViewClassDetails(classItem?.batchId)
                                }
                                title="View Students & Teachers"
                                data-bs-toggle="modal"
                                data-bs-target="#class_details_modal"
                              >
                                <i className="ti ti-users-group me-1"></i>{" "}
                                Details
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary me-2"
                                onClick={() => {
                                  navigation(
                                    `${all_routes.timetable}?id=${classItem?.batchId}&curriculumCourseId=${classItem?.curriculumCourseId}`
                                  );
                                }}
                                title="View Timetable"
                              >
                                <i className="ti ti-calendar me-1"></i>{" "}
                                Timetable
                              </button>
                              <Link
                                hidden={
                                  !hasAcademicMgntEdit() ||
                                  isBeforeCurrentDate(
                                    classItem.curriculumEndDate
                                  ) ||
                                  (classItem.curriculumStatus !== "ACTIVE" &&
                                    classItem.curriculumStatus !== "UPCOMING")
                                }
                                to="#"
                                className="btn btn-sm btn-outline-primary me-2"
                                data-bs-toggle="modal"
                                data-inert={true}
                                data-bs-target="#edit_class"
                                onClick={() => {
                                  setEditId(classItem?.batchId);
                                }}
                                title="Edit Class"
                              >
                                <i className="ti ti-edit me-1" /> Edit
                              </Link>
                              <Link
                                hidden={
                                  !hasClassMgntDelete() ||
                                  classItem.curriculumStatus === "INACTIVE"
                                }
                                to="#"
                                className="btn btn-sm btn-outline-danger"
                                data-bs-toggle="modal"
                                data-inert={true}
                                data-bs-target="#delete_modal"
                                onClick={() =>
                                  setDeleteItem({
                                    id: classItem.batchId,
                                    message: `Are you sure you want to delete ${classItem.batchName}? This can't be undone.`,
                                  })
                                }
                                title="Delete Class"
                              >
                                <i className="ti ti-trash me-1" /> Delete
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>
      <div className="modal fade" id="add_class">
        {renderAddClass()}
      </div>
      <div className="modal fade" id="edit_class">
        {renderEditClass()}
      </div>
      <div className="modal fade" id="class_details_modal">
        {renderClassDetailsModal()}
      </div>
    </>
  );
};

function parseToClasses(data: any[]) {
  let classes: Class[] = [];
  if (data) {
    data.forEach((item) => {
      let existing = classes.find(
        (el: Class, idx) => item.batchId === el.batchId
      );
      if (!existing) {
        classes.push({
          batchId: item.batchId,
          batchName: item.batchName,
          curriculumTitle: item.curriculumTitle,
          courseTitle: item.courseTitle,
          batchSection: item.batchSection,
          curriculumCourseId: item.curriculumCourseId,
          courseSubjectId: item.id.courseSubjectId,
          curriculumStatus: item.curriculumStatus,
          curriculumStartDate: item.curriculumStartDate,
          curriculumEndDate: item.curriculumEndDate,
        });
      }
    });
  }
  return classes;
}

function parseToOption(currCourse: Curriculum360[]) {
  return currCourse.map((item: Curriculum360, idx: number) => {
    return {
      label: `${item.courseTitle} - ${item.curriculumTitle}`,
      data: item,
      value: `${item.curriculumCourseId}`,
    };
  });
}

function parseToCurrCourse(data: any[]) {
  let currCourse: Curriculum360[] = [];
  if (data) {
    data.forEach((item) => {
      if (item && item.curriculumStatus !== "INACTIVE") {
        let existing = currCourse.find(
          (el: Curriculum360, idx) =>
            item.curriculumCourseId === el.curriculumCourseId
        );
        if (!existing) {
          currCourse.push({
            curriculumCourseId: item.curriculumCourseId,
            curriculumTitle: item.curriculumTitle,
            courseTitle: item.courseTitle,
            curriculumStatus: item.curriculumStatus,
          });
        }
      }
    });
  }
  return currCourse;
}

export default Classes;
